/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'

import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
  Fragment,
} from 'react'
import { useRegistrationSetupContext } from '../../context'
import { App, Tooltip, Dropdown } from 'antd'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { getFormItemTypeId, getQuestionType } from '../customFormTypeMappings'
import DraggableSection from '../CustomForm/DraggableSection'
import DeleteSectionModal from '../CustomForm/DeleteSectionModal'
import DeleteQuestionModal from '../CustomForm/DeleteQuestionModal'
import SaveIcon from '../CustomForm/SaveIcon'
import CancelIcon from '../CustomForm/CancelIcon'
import PreviewIcon from '../CustomForm/PreviewIcon'
import RichTextEditor from '../CustomForm/RichTextEditor'
import { sanitizeContentForSave } from 'src/utils/sanitizeContentForSave'

import { LegacyQuestion } from '../CustomForm/types'
import {
  CustomForm,
  Group,
  FormItem,
  convertOptionsToParameters,
  convertParametersToOptions,
  // FORM_ITEM_TYPE_OPTIONS,
} from '@sportninja/common/hooks/useCustomForm'

type Question = LegacyQuestion

type Section = {
  section_id: string
  title: string
  subtitle: string
  order: number
  enabled: boolean
  questions: Question[]
  is_reserved: boolean
  is_content?: boolean
  content?: string
}

// Convert FormItem to LegacyQuestion for compatibility with existing UI components
const formItemToLegacyQuestion = (item: FormItem): LegacyQuestion => {
  // Convert parameters to min/max values for number questions
  const convertParametersToNumberRange = (
    parameters?: FormItem['parameters']
  ) => {
    if (!parameters) {
      return { min: undefined, max: undefined }
    }

    // Handle both old and new parameter formats
    const minValue = parameters.min_number || parameters.min
    const maxValue = parameters.max_number || parameters.max

    return {
      min: minValue ? parseInt(minValue as string, 10) : undefined,
      max: maxValue ? parseInt(maxValue as string, 10) : undefined,
    }
  }

  // Convert parameters to max_char value for text questions
  const convertParametersToMaxChar = (parameters?: FormItem['parameters']) => {
    if (!parameters?.max_char) {
      return undefined
    }
    return parseInt(parameters.max_char as string, 10)
  }

  const questionType = getQuestionType(item.form_item_type.id) // Use form_item_type.id instead of form_item_type_id
  const isChoiceType = ['dropdown', 'multi_select'].includes(questionType) // dropdown + multi-select have options
  const isNumberType = questionType === 'number' // Number type has min/max parameters
  const isTextType = ['text', 'textarea', 'email', 'url'].includes(questionType) // Text types support max_char

  const numberRange = isNumberType
    ? convertParametersToNumberRange(item.parameters)
    : { min: undefined, max: undefined }

  const maxChar = isTextType
    ? convertParametersToMaxChar(item.parameters)
    : undefined

  return {
    field_id: item.id,
    label: item.name,
    type: questionType,
    subtype: questionType,
    mandatory: Boolean(item.is_required), // Convert numeric boolean to actual boolean
    placeholder: item.description || '',
    description: item.description || '',
    order: item.index,
    enabled: Boolean(item.is_enabled), // Convert numeric boolean to actual boolean
    options: isChoiceType ? convertParametersToOptions(item.parameters) : [],
    min: numberRange.min,
    max: numberRange.max,
    max_char: maxChar,
    isReserved: Boolean(item.is_reserved),
  }
}

// Convert Group to Section for compatibility with existing UI components
const groupToSection = (group: Group): Section => ({
  section_id: group.id,
  title: group.name,
  subtitle: group.description || '',
  order: group.index,
  enabled: Boolean(group.is_enabled), // Convert numeric boolean to actual boolean
  questions: group.items ? group.items.map(formItemToLegacyQuestion) : [],
  is_reserved: Boolean(group.is_reserved),
  is_content: Boolean((group as any).is_content),
  content: (group as any).content || '',
})

// Helper function to check if rich text content is truly empty
const isRichTextEmpty = (content: string): boolean => {
  if (!content || content.trim() === '') {
    return true
  }
  // Strip HTML tags and check if remaining text is empty
  const strippedText = content.replace(/<[^>]*>/g, '').trim()
  return strippedText.length === 0
}

interface Props {}

const TeamCustomForm = forwardRef<{ hasPendingChanges: () => boolean }, Props>(
  (_props, ref) => {
    const {
      state: registrationState,
      scheduleId,
      isUpdated,
      patchTeamForm,
      formMutations,
    } = useRegistrationSetupContext()
    const {
      createGroup,
      updateGroup,
      deleteGroup,
      reorderGroups,
      createItem,
      updateItem,
      deleteItem,
      reorderItems,
    } = formMutations
    const [showForm, setShowForm] = useState(false)
    const [showContentForm, setShowContentForm] = useState(false)
    // Section that a new Section/Content will be inserted AFTER (null = append/empty-state)
    const [insertAfterId, setInsertAfterId] = useState<string | null>(null)
    const [formData, setFormData] = useState({
      title: '',
      subtitle: '',
    })
    const [contentData, setContentData] = useState('')
    const [contentSectionName, setContentSectionName] = useState('')

    // DragOverlay state for smooth dragging
    const [activeSectionId, setActiveSectionId] = useState<string | null>(null)

    // Auto-scroll state for newly created sections
    const [scrollToSectionId, setScrollToSectionId] = useState<string | null>(
      null
    )

    // Optimistic state for section enabled status
    const [optimisticSectionEnabled, setOptimisticSectionEnabled] = useState<
      Record<string, boolean>
    >({})

    // Use the form from context instead of fetching separately
    const form = registrationState.team_form

    // Delete modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [sectionToDelete, setSectionToDelete] = useState<Section | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [deleteError, setDeleteError] = useState<string | null>(null)
    const [isDeleteQuestionModalOpen, setIsDeleteQuestionModalOpen] =
      useState(false)
    const [questionToDelete, setQuestionToDelete] = useState<{
      sectionId: string
      questionId: string
      label: string
    } | null>(null)
    const [isDeletingQuestion, setIsDeletingQuestion] = useState(false)
    const [deleteQuestionError, setDeleteQuestionError] = useState<
      string | null
    >(null)

    // Run a mutation and apply its response to state.team_form via the
    // context patcher. Avoids the registration refetch (and the resulting
    // hydration clobber) that the previous onFormChange-based flow caused.
    const runAndRefresh = useCallback(
      async <T,>(
        fn: () => Promise<T>,
        patch?: (result: T, form: CustomForm) => CustomForm
      ): Promise<T> => {
        const result = await fn()
        if (patch) {
          patchTeamForm((form) => patch(result, form))
        }
        return result
      },
      [patchTeamForm]
    )

    // Convert API groups to sections for UI compatibility with optimistic updates
    const sections = useMemo(
      () =>
        form?.groups
          ? (form.groups as Group[])
              .map(groupToSection)
              .map((section) => ({
                ...section,
                // Apply optimistic enabled override if it exists
                enabled:
                  optimisticSectionEnabled[section.section_id] !== undefined
                    ? optimisticSectionEnabled[section.section_id]
                    : section.enabled,
              }))
              .sort((a, b) => a.order - b.order)
          : [],
      [form?.groups, optimisticSectionEnabled]
    )

    // Active section for DragOverlay
    const activeSection = useMemo(
      () => sections.find((s) => s.section_id === activeSectionId),
      [sections, activeSectionId]
    )

    // Debug: Log sections with questions
    useEffect(() => {
      if (sections.length > 0) {
        console.log(
          'Sections with questions:',
          sections.map((s) => ({
            id: s.section_id,
            title: s.title,
            questionCount: s.questions.length,
            questions: s.questions,
          }))
        )
      }
    }, [sections])

    // Auto-scroll to newly created section
    useEffect(() => {
      if (!scrollToSectionId) {
        return
      }

      console.log('🔄 Auto-scrolling to section:', scrollToSectionId)
      const element = document.getElementById(scrollToSectionId)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setScrollToSectionId(null) // Reset so it only runs once
      }
    }, [sections, scrollToSectionId])

    const { state, setCustomErroByField, clearCustomErrors } =
      useRegistrationSetupContext()
    const { message } = App.useApp()

    // Create refs for sections to track their pending changes
    const sectionRefs = useRef<
      Record<string, { hasPendingChanges: () => boolean } | null>
    >({})

    // Configure DnD sensors
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    )

    // Handle drag start event
    const handleDragStart = useCallback((event: DragStartEvent) => {
      console.log('🔄 handleDragStart: setting active section', event.active.id)
      setActiveSectionId(event.active.id as string)
    }, [])

    // Handle drag end event - TODO: Implement API call to update group order
    const handleDragEnd = useCallback(
      async (event: DragEndEvent) => {
        const { active, over } = event

        // Clear active section
        setActiveSectionId(null)

        if (over && active.id !== over.id) {
          try {
            const oldIndex = sections.findIndex(
              (section) => section.section_id === active.id
            )
            const newIndex = sections.findIndex(
              (section) => section.section_id === over.id
            )

            if (oldIndex !== -1 && newIndex !== -1) {
              // Reorder sections locally to calculate new indices
              const reorderedSections = arrayMove(sections, oldIndex, newIndex)

              // Create the payload for the API
              const reorderPayload = reorderedSections.map(
                (section, index) => ({
                  id: section.section_id,
                  index: index + 1, // API expects 1-based indexing
                })
              )

              // Call the API to update the order
              await runAndRefresh(
                () => reorderGroups(form?.id || '', reorderPayload),
                (_void, currentForm) => {
                  const orderById = new Map(
                    reorderPayload.map((g) => [g.id, g.index])
                  )
                  return {
                    ...currentForm,
                    groups: [...((currentForm.groups as Group[]) ?? [])]
                      .map((g) => ({
                        ...g,
                        index: orderById.get(g.id) ?? g.index,
                      }))
                      .sort((a, b) => a.index - b.index),
                  }
                }
              )
            }
          } catch (error) {
            message.error('Failed to update section order. Please try again.')
          }
        }
      },
      [sections, reorderGroups, message, form?.id, runAndRefresh]
    )

    // Handle drag cancel event
    const handleDragCancel = useCallback(() => {
      console.log('🔄 handleDragCancel: clearing active section')
      setActiveSectionId(null)
    }, [])

    const handleAddSection = () => {
      clearCustomErrors()
      setInsertAfterId(null)
      setShowForm(true)
      setShowContentForm(false)
    }

    const handleAddContent = () => {
      clearCustomErrors()
      setInsertAfterId(null)
      setShowContentForm(true)
      setShowForm(false)
      setContentData('')
    }

    const handleInsertSection = (afterSectionId: string) => {
      clearCustomErrors()
      setInsertAfterId(afterSectionId)
      setShowForm(true)
      setShowContentForm(false)
      setFormData({ title: '', subtitle: '' })
    }

    const handleInsertContent = (afterSectionId: string) => {
      clearCustomErrors()
      setInsertAfterId(afterSectionId)
      setShowContentForm(true)
      setShowForm(false)
      setContentData('')
      setContentSectionName('')
    }

    const handleCancel = () => {
      setShowForm(false)
      setShowContentForm(false)
      setFormData({ title: '', subtitle: '' })
      setContentData('')
      setInsertAfterId(null)
      clearCustomErrors()
    }

    // 1-based index to create a new group at: right after `insertAfterId` when
    // set, otherwise appended at the end. reorderAfterInsert still runs the
    // authoritative reindex, but sending the correct index up front keeps the
    // create request accurate.
    const computeCreateIndex = (): number => {
      if (insertAfterId) {
        const idx = sections.findIndex((s) => s.section_id === insertAfterId)
        if (idx !== -1) {
          return idx + 2 // anchor is 1-based idx+1; new group goes right after
        }
      }
      return sections.length + 1
    }

    // After creating a group, place it right after `afterId` and push a correct
    // sequential 1-based index for every group to the API.
    const reorderAfterInsert = async (
      createdId: string,
      afterId: string | null
    ) => {
      const currentIds = sections.map((s) => s.section_id)
      const withoutNew = currentIds.filter((sid) => sid !== createdId)
      let insertAt = withoutNew.length
      if (afterId) {
        const idx = withoutNew.indexOf(afterId)
        if (idx !== -1) {
          insertAt = idx + 1
        }
      }
      const ordered = [
        ...withoutNew.slice(0, insertAt),
        createdId,
        ...withoutNew.slice(insertAt),
      ]
      const reorderPayload = ordered.map((sid, index) => ({
        id: sid,
        index: index + 1,
      }))
      await runAndRefresh(
        () => reorderGroups(form?.id || '', reorderPayload),
        (_result, currentForm) => {
          const orderById = new Map(reorderPayload.map((g) => [g.id, g.index]))
          return {
            ...currentForm,
            groups: ((currentForm.groups as Group[]) ?? []).map((g) => ({
              ...g,
              index: orderById.get(g.id) ?? g.index,
            })),
          }
        }
      )
    }

    const handleSave = async () => {
      clearCustomErrors()
      if (formData.title.trim() === '') {
        setCustomErroByField('title', 'Title is required')
      }

      if (formData.title.trim()) {
        try {
          const nextIndex = computeCreateIndex()
          const newGroup = (await runAndRefresh(
            () =>
              createGroup({
                form_id: form?.id || '',
                name: formData.title,
                description: formData.subtitle || undefined,
                is_enabled: true,
                is_reserved: false,
                index: nextIndex,
              }),
            (created, currentForm) => ({
              ...currentForm,
              groups: [
                ...((currentForm.groups as Group[]) ?? []),
                { ...(created as Group), items: [] },
              ],
            })
          )) as Group | undefined

          if (newGroup?.id && insertAfterId) {
            await reorderAfterInsert(newGroup.id, insertAfterId)
          }

          // Set the new section ID for auto-scrolling
          if (newGroup?.id) {
            console.log('🔄 Setting scroll target to new section:', newGroup.id)
            setScrollToSectionId(newGroup.id)
          }

          setFormData({ title: '', subtitle: '' })
          setShowForm(false)
          setInsertAfterId(null)
        } catch (error) {
          message.error('Failed to create section. Please try again.')
        }
      }
    }

    const handleSaveContent = async () => {
      clearCustomErrors()
      if (contentSectionName.trim() === '') {
        message.error('Section name is required')
        return
      }
      if (isRichTextEmpty(contentData)) {
        message.error('Content is required')
        return
      }

      try {
        const nextIndex = computeCreateIndex()
        const newGroup = (await runAndRefresh(
          () =>
            createGroup({
              form_id: form?.id || '',
              name: contentSectionName,
              description: undefined,
              is_enabled: true,
              is_reserved: false,
              index: nextIndex,
              is_content: true,
              content: sanitizeContentForSave(contentData),
            } as any),
          (created, currentForm) => ({
            ...currentForm,
            groups: [
              ...((currentForm.groups as Group[]) ?? []),
              { ...(created as Group), items: [] },
            ],
          })
        )) as Group | undefined

        if (newGroup?.id && insertAfterId) {
          await reorderAfterInsert(newGroup.id, insertAfterId)
        }

        // Set the new section ID for auto-scrolling
        if (newGroup?.id) {
          console.log(
            '🔄 Setting scroll target to new content section:',
            newGroup.id
          )
          setScrollToSectionId(newGroup.id)
        }

        setContentData('')
        setContentSectionName('')
        setShowContentForm(false)
        setInsertAfterId(null)
      } catch (error) {
        message.error('Failed to create content section. Please try again.')
      }
    }

    // Handle section toggle with optimistic update
    const handleToggle = useCallback(
      async (id: string, enabled: boolean) => {
        const section = sections.find((s) => s.section_id === id)

        if (!section) {
          message.error('Section not found.')
          return
        }

        // Optimistic update: set state immediately
        setOptimisticSectionEnabled((prev) => ({
          ...prev,
          [id]: enabled,
        }))

        const payload = {
          name: section.title,
          description: section.subtitle,
          is_enabled: enabled,
        }

        try {
          await runAndRefresh(
            () => updateGroup(id, payload),
            (updated, currentForm) => ({
              ...currentForm,
              groups: ((currentForm.groups as Group[]) ?? []).map((g) =>
                g.id === id ? { ...g, ...updated } : g
              ),
            })
          )
        } catch (error) {
          // Rollback on error
          setOptimisticSectionEnabled((prev) => {
            const newState = { ...prev }
            delete newState[id]
            return newState
          })
          message.error('Failed to toggle section. Please try again.')
        }
      },
      [sections, updateGroup, message, runAndRefresh]
    )

    const handleEdit = useCallback(
      async (
        id: string,
        newTitle: string,
        newSubtitle: string,
        newContent?: string
      ) => {
        try {
          const payload: any = {
            name: newTitle,
            description: newSubtitle,
          }

          // Add content field for content sections
          if (newContent !== undefined) {
            payload.content = sanitizeContentForSave(newContent)
          }

          await runAndRefresh(
            () => updateGroup(id, payload),
            (updated, currentForm) => ({
              ...currentForm,
              groups: ((currentForm.groups as Group[]) ?? []).map((g) =>
                g.id === id ? { ...g, ...updated } : g
              ),
            })
          )
        } catch (error) {
          message.error('Failed to update section. Please try again.')
        }
      },
      [updateGroup, message, runAndRefresh]
    )

    const handleNewQuestion = useCallback((id: string) => {
      // This is now handled by the DraggableSection component
      console.log('New question for section:', id)
    }, [])

    // Handle delete modal
    const handleDeleteSection = useCallback(
      (id: string) => {
        const section = sections.find((s) => s.section_id === id)
        if (section) {
          setSectionToDelete(section)
          setIsDeleteModalOpen(true)
          setDeleteError(null)
        }
      },
      [sections]
    )

    const handleConfirmDelete = useCallback(async () => {
      if (!sectionToDelete) {
        return
      }

      setIsDeleting(true)
      setDeleteError(null)

      try {
        const removedId = sectionToDelete.section_id
        await runAndRefresh(
          () => deleteGroup(removedId),
          (_void, currentForm) => ({
            ...currentForm,
            groups: ((currentForm.groups as Group[]) ?? []).filter(
              (g) => g.id !== removedId
            ),
          })
        )
        setIsDeleteModalOpen(false)
        setSectionToDelete(null)
      } catch (error) {
        setDeleteError('Failed to delete section. Please try again.')
      } finally {
        setIsDeleting(false)
      }
    }, [sectionToDelete, deleteGroup, runAndRefresh])

    const handleCancelDelete = useCallback(() => {
      setIsDeleteModalOpen(false)
      setSectionToDelete(null)
      setDeleteError(null)
    }, [])

    // Handle updating questions in a section (mainly for reordering)
    const handleUpdateQuestions = useCallback(
      async (sectionId: string, questions: LegacyQuestion[]) => {
        try {
          // Create reorder payload based on the new order
          const reorderPayload = questions.map((question, index) => ({
            id: question.field_id,
            index: index + 1, // API expects 1-based indexing
          }))

          await runAndRefresh(
            () => reorderItems(sectionId, reorderPayload),
            (_void, currentForm) => {
              const orderById = new Map(
                reorderPayload.map((it) => [it.id, it.index])
              )
              return {
                ...currentForm,
                groups: ((currentForm.groups as Group[]) ?? []).map((g) =>
                  g.id === sectionId
                    ? {
                        ...g,
                        items: [...((g.items as FormItem[]) ?? [])]
                          .map((it) => ({
                            ...it,
                            index: orderById.get(it.id) ?? it.index,
                          }))
                          .sort((a, b) => a.index - b.index),
                      }
                    : g
                ),
              }
            }
          )
        } catch (error) {
          message.error('Failed to reorder questions. Please try again.')
        }
      },
      [reorderItems, message, runAndRefresh]
    )

    // Handle question toggle
    const handleQuestionToggle = useCallback(
      async (sectionId: string, questionId: string, enabled: boolean) => {
        const section = sections.find((s) => s.section_id === sectionId)
        const question = section?.questions.find(
          (q) => q.field_id === questionId
        )

        if (!question) {
          message.error('Question not found.')
          return
        }

        const payload = {
          name: question.label,
          form_item_type_id: getFormItemTypeId(question.type),
          is_enabled: enabled,
        }

        try {
          await runAndRefresh(
            () => updateItem(questionId, payload),
            (updated, currentForm) => ({
              ...currentForm,
              groups: ((currentForm.groups as Group[]) ?? []).map((g) =>
                g.id === sectionId
                  ? {
                      ...g,
                      items: ((g.items as FormItem[]) ?? []).map((it) =>
                        it.id === questionId ? { ...it, ...updated } : it
                      ),
                    }
                  : g
              ),
            })
          )
        } catch (error) {
          message.error('Failed to toggle question. Please try again.')
        }
      },
      [sections, updateItem, message, runAndRefresh]
    )

    // Handle question delete
    const handleQuestionDelete = useCallback(
      (sectionId: string, questionId: string) => {
        const section = sections.find((s) => s.section_id === sectionId)
        const question = section?.questions.find(
          (q) => q.field_id === questionId
        )

        if (!question) {
          message.error('Question not found.')
          return
        }

        setDeleteQuestionError(null)
        setQuestionToDelete({
          sectionId,
          questionId,
          label: question.label,
        })
        setIsDeleteQuestionModalOpen(true)
      },
      [sections, message]
    )

    const handleConfirmQuestionDelete = useCallback(async () => {
      if (!questionToDelete) {
        return
      }

      setIsDeletingQuestion(true)
      setDeleteQuestionError(null)

      try {
        const removedSectionId = questionToDelete.sectionId
        const removedQuestionId = questionToDelete.questionId
        await runAndRefresh(
          () => deleteItem(removedQuestionId),
          (_void, currentForm) => ({
            ...currentForm,
            groups: ((currentForm.groups as Group[]) ?? []).map((g) =>
              g.id === removedSectionId
                ? {
                    ...g,
                    items: ((g.items as FormItem[]) ?? []).filter(
                      (it) => it.id !== removedQuestionId
                    ),
                  }
                : g
            ),
          })
        )
        setIsDeleteQuestionModalOpen(false)
        setQuestionToDelete(null)
      } catch (error) {
        setDeleteQuestionError('Failed to delete question. Please try again.')
      } finally {
        setIsDeletingQuestion(false)
      }
    }, [questionToDelete, deleteItem, runAndRefresh])

    const handleCancelQuestionDelete = useCallback(() => {
      if (isDeletingQuestion) {
        return
      }

      setIsDeleteQuestionModalOpen(false)
      setQuestionToDelete(null)
      setDeleteQuestionError(null)
    }, [isDeletingQuestion])

    // Handle adding a new question to a section
    const handleAddQuestion = useCallback(
      async (sectionId: string, question: Question) => {
        try {
          // Convert legacy options to parameters format for choice questions
          // Using shared utility function to ensure value always matches label

          // Convert legacy number range to parameters format for number questions
          const convertNumberRangeToParameters = (
            min?: number,
            max?: number
          ) => {
            if (min === undefined && max === undefined) {
              return undefined
            }

            return {
              min_number: min?.toString(),
              max_number: max?.toString(),
            }
          }

          // Convert legacy max_char to parameters format for text questions
          const convertMaxCharToParameters = (max_char?: number) => {
            if (max_char === undefined) {
              return undefined
            }

            return {
              max_char: max_char.toString(),
            }
          }

          // Get the current group to determine the next index
          const group = (form?.groups as Group[])?.find(
            (g: Group) => g.id === sectionId
          )
          const nextIndex = group?.items ? group.items.length + 1 : 1

          const isChoiceType = ['dropdown', 'multi_select'].includes(
            question.type
          ) // dropdown + multi-select have options
          const isNumberType = question.type === 'number' // Number type has min/max parameters
          const isPlayerNumberType = question.type === 'player_number' // Player number type doesn't need parameters
          const isTextType = ['text', 'textarea', 'email', 'url'].includes(
            question.type
          ) // Text types support max_char

          let parameters:
            | {
                values?: Array<{
                  name: string
                  value: string
                  index: number
                }>
                min_number?: string | number
                max_number?: string | number
                min?: string | number
                max?: string | number
                max_char?: string | number
              }
            | undefined

          if (isChoiceType) {
            parameters = convertOptionsToParameters(question.options || [])
          } else if (isNumberType) {
            parameters = convertNumberRangeToParameters(
              question.min,
              question.max
            )
          } else if (isPlayerNumberType) {
            // Player number fields don't need parameters
            parameters = undefined
          } else if (isTextType && question.max_char) {
            parameters = convertMaxCharToParameters(question.max_char)
          }

          await runAndRefresh(
            () =>
              createItem(sectionId, {
                name: question.label,
                description: question.description || undefined,
                form_item_type_id: getFormItemTypeId(question.type),
                is_enabled: question.enabled ?? true,
                is_required: question.mandatory,
                is_reserved: false,
                index: nextIndex,
                parameters,
              }),
            (created, currentForm) => ({
              ...currentForm,
              groups: ((currentForm.groups as Group[]) ?? []).map((g) =>
                g.id === sectionId
                  ? {
                      ...g,
                      items: [...((g.items as FormItem[]) ?? []), created],
                    }
                  : g
              ),
            })
          )
        } catch (error) {
          message.error('Failed to add question. Please try again.')
        }
      },
      [form?.groups, createItem, message, runAndRefresh]
    )

    // Handle updating a question in a section
    const handleQuestionUpdate = useCallback(
      async (
        sectionId: string,
        questionId: string,
        updatedQuestion: Partial<LegacyQuestion>
      ) => {
        try {
          // Convert legacy options to parameters format for choice questions
          // Using shared utility function to ensure value always matches label

          // Convert legacy number range to parameters format for number questions
          const convertNumberRangeToParameters = (
            min?: number,
            max?: number
          ) => {
            if (min === undefined && max === undefined) {
              return undefined
            }

            return {
              min_number: min?.toString(),
              max_number: max?.toString(),
            }
          }

          // Convert legacy max_char to parameters format for text questions
          const convertMaxCharToParameters = (max_char?: number) => {
            if (max_char === undefined) {
              return undefined
            }

            return {
              max_char: max_char.toString(),
            }
          }

          const updateData: any = {}

          if (updatedQuestion.label !== undefined) {
            updateData.name = updatedQuestion.label
          }
          if (updatedQuestion.description !== undefined) {
            updateData.description = updatedQuestion.description
          }
          if (updatedQuestion.type !== undefined) {
            updateData.form_item_type_id = getFormItemTypeId(
              updatedQuestion.type
            )
          }
          if (updatedQuestion.enabled !== undefined) {
            updateData.is_enabled = updatedQuestion.enabled
          }
          if (updatedQuestion.mandatory !== undefined) {
            updateData.is_required = updatedQuestion.mandatory
          }

          // Handle options for dropdown questions
          if (updatedQuestion.options !== undefined) {
            const questionType = updatedQuestion.type
            const isChoiceType =
              !!questionType &&
              ['dropdown', 'multi_select'].includes(questionType) // dropdown + multi-select have options

            if (isChoiceType) {
              updateData.parameters = convertOptionsToParameters(
                updatedQuestion.options
              )
            }
          }

          // Handle min/max for number questions
          if (
            updatedQuestion.min !== undefined ||
            updatedQuestion.max !== undefined
          ) {
            const questionType = updatedQuestion.type
            const isNumberType = questionType === 'number' // Number type has min/max parameters

            if (isNumberType) {
              updateData.parameters = convertNumberRangeToParameters(
                updatedQuestion.min,
                updatedQuestion.max
              )
            }
          }

          // Handle max_char for text questions
          if (updatedQuestion.max_char !== undefined) {
            const questionType = updatedQuestion.type
            const isTextType = ['text', 'textarea', 'email', 'url'].includes(
              questionType || ''
            ) // Text types support max_char

            if (isTextType) {
              updateData.parameters = {
                ...updateData.parameters,
                ...convertMaxCharToParameters(updatedQuestion.max_char),
              }
            }
          }

          await runAndRefresh(
            () => updateItem(questionId, updateData),
            (updated, currentForm) => ({
              ...currentForm,
              groups: ((currentForm.groups as Group[]) ?? []).map((g) =>
                g.id === sectionId
                  ? {
                      ...g,
                      items: ((g.items as FormItem[]) ?? []).map((it) =>
                        it.id === questionId ? { ...it, ...updated } : it
                      ),
                    }
                  : g
              ),
            })
          )
        } catch (error) {
          message.error('Failed to update question. Please try again.')
        }
      },
      [updateItem, message, runAndRefresh]
    )

    // Expose the hasPendingChanges method to parent
    useImperativeHandle(
      ref,
      () => ({
        hasPendingChanges: () => {
          const formOpen = showForm || showContentForm
          const sectionChanges = Object.values(sectionRefs.current).some(
            (ref) => ref?.hasPendingChanges?.()
          )
          console.log('🔄 TeamCustomForm hasPendingChanges:', {
            formOpen,
            showContentForm,
            sectionChanges,
            total: formOpen || sectionChanges,
          })
          return formOpen || sectionChanges
        },
      }),
      [showForm, showContentForm]
    )

    const renderAddSectionForm = () => (
      <div
        css={css`
          align-self: stretch;
          padding: 8px;
          background: ${colors.SECONDARY_400};
          border-radius: 8px;
          justify-content: flex-start;
          align-items: center;
          gap: 8px;
          display: inline-flex;
        `}
      >
        <div
          css={css`
            flex: 1 1 0;
            align-self: stretch;
            justify-content: flex-start;
            align-items: center;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              flex: 1 1 0;
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              gap: 8px;
              display: inline-flex;
            `}
          >
            <div
              css={css`
                align-self: stretch;
                justify-content: flex-start;
                align-items: flex-start;
                gap: 16px;
                display: inline-flex;
              `}
            >
              <div
                css={css`
                  flex: 1 1 0;
                  flex-direction: column;
                  justify-content: flex-start;
                  align-items: flex-start;
                  gap: 8px;
                  display: inline-flex;
                `}
              >
                <TextInput
                  name='title'
                  value={formData.title}
                  setValue={(value) =>
                    setFormData({ ...formData, title: value })
                  }
                  fieldErrors={state.fieldErrors}
                  customError={state.customError}
                  label='Title *'
                  placeholder='Enter section title'
                />
              </div>
            </div>

            <div
              css={css`
                align-self: stretch;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                gap: 8px;
                display: inline-flex;
              `}
            >
              <TextInput
                name='subtitle'
                value={formData.subtitle}
                setValue={(value) =>
                  setFormData({ ...formData, subtitle: value })
                }
                fieldErrors={state.fieldErrors}
                customError={state.customError}
                label='Subtitle'
                placeholder='Enter section subtitle (optional)'
              />
            </div>

            <div
              css={css`
                align-self: stretch;
                height: 32px;
                justify-content: flex-end;
                align-items: center;
                gap: 8px;
                display: inline-flex;
                margin-top: 8px;
              `}
            >
              <button
                css={css`
                  border: 1px solid ${colors.NEUTRAL_0};
                  display: flex;
                  padding: 4px 8px;
                  align-items: center;
                  gap: 4px;
                  border-radius: 8px;
                  background: transparent;
                  cursor: pointer;
                  &:hover {
                    opacity: 0.9;
                  }
                `}
                onClick={handleCancel}
              >
                <CancelIcon size={24} color={colors.NEUTRAL_0} />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                >
                  Cancel
                </Text>
              </button>
              <button
                css={css`
                  border: 1px solid
                    ${formData.title.trim()
                      ? colors.PRIMARY
                      : colors.NEUTRAL_30};
                  display: flex;
                  padding: 4px 8px;
                  align-items: center;
                  gap: 4px;
                  border-radius: 8px;
                  background: transparent;
                  cursor: ${formData.title.trim() ? 'pointer' : 'not-allowed'};
                  opacity: ${formData.title.trim() ? 1 : 0.5};
                  &:hover {
                    opacity: ${formData.title.trim() ? 0.9 : 0.5};
                  }
                `}
                onClick={handleSave}
                disabled={!formData.title.trim()}
              >
                <SaveIcon
                  size={24}
                  color={
                    formData.title.trim() ? colors.PRIMARY : colors.NEUTRAL_30
                  }
                />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={
                    formData.title.trim() ? colors.PRIMARY : colors.NEUTRAL_30
                  }
                >
                  Save
                </Text>
              </button>
            </div>
          </div>
        </div>
      </div>
    )

    const renderAddContentForm = () => (
      <div
        css={css`
          align-self: stretch;
          padding: 8px;
          background: ${colors.SECONDARY_400};
          border-radius: 8px;
          justify-content: flex-start;
          align-items: center;
          gap: 8px;
          display: inline-flex;
        `}
      >
        <div
          css={css`
            flex: 1 1 0;
            align-self: stretch;
            justify-content: flex-start;
            align-items: center;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              flex: 1 1 0;
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              gap: 8px;
              display: inline-flex;
            `}
          >
            <div
              css={css`
                align-self: stretch;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                gap: 8px;
                display: inline-flex;
              `}
            >
              <TextInput
                name='content-section-name'
                value={contentSectionName}
                setValue={setContentSectionName}
                fieldErrors={state.fieldErrors}
                customError={state.customError}
                label='Section Name *'
                placeholder='Enter section name'
              />
            </div>
            <RichTextEditor
              value={contentData}
              onChange={setContentData}
              label='Content *'
              placeholder='Enter rich text content...'
            />

            <div
              css={css`
                align-self: stretch;
                height: 32px;
                justify-content: flex-end;
                align-items: center;
                gap: 8px;
                display: inline-flex;
                margin-top: 8px;
              `}
            >
              <button
                css={css`
                  border: 1px solid ${colors.NEUTRAL_0};
                  display: flex;
                  padding: 4px 8px;
                  align-items: center;
                  gap: 4px;
                  border-radius: 8px;
                  background: transparent;
                  cursor: pointer;
                  &:hover {
                    opacity: 0.9;
                  }
                `}
                onClick={handleCancel}
              >
                <CancelIcon size={24} color={colors.NEUTRAL_0} />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                >
                  Cancel
                </Text>
              </button>
              <button
                css={css`
                  border: 1px solid
                    ${contentSectionName.trim() && !isRichTextEmpty(contentData)
                      ? colors.PRIMARY
                      : colors.NEUTRAL_30};
                  display: flex;
                  padding: 4px 8px;
                  align-items: center;
                  gap: 4px;
                  border-radius: 8px;
                  background: transparent;
                  cursor: ${contentSectionName.trim() &&
                  !isRichTextEmpty(contentData)
                    ? 'pointer'
                    : 'not-allowed'};
                  opacity: ${contentSectionName.trim() &&
                  !isRichTextEmpty(contentData)
                    ? 1
                    : 0.5};
                  &:hover {
                    opacity: ${contentSectionName.trim() &&
                    !isRichTextEmpty(contentData)
                      ? 0.9
                      : 0.5};
                  }
                `}
                onClick={handleSaveContent}
                disabled={
                  !contentSectionName.trim() || isRichTextEmpty(contentData)
                }
              >
                <SaveIcon
                  size={24}
                  color={
                    contentSectionName.trim() && !isRichTextEmpty(contentData)
                      ? colors.PRIMARY
                      : colors.NEUTRAL_30
                  }
                />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={
                    contentSectionName.trim() && !isRichTextEmpty(contentData)
                      ? colors.PRIMARY
                      : colors.NEUTRAL_30
                  }
                >
                  Save
                </Text>
              </button>
            </div>
          </div>
        </div>
      </div>
    )

    return (
      <div
        css={css`
          display: flex;
          gap: 16px;
          flex-direction: column;
          align-items: flex-end;
          padding-bottom: 100px;
          width: 100%;
        `}
      >
        {/* Header with title and buttons */}
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            align-self: stretch;
            width: 100%;
          `}
        >
          <Text variant='captionLarge' weight='semiBold'>
            Team Custom Form
          </Text>

          <div
            css={css`
              align-self: stretch;
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              gap: 16px;
              display: inline-flex;
            `}
          >
            <div
              css={css`
                justify-content: flex-start;
                align-items: center;
                gap: 16px;
                display: inline-flex;
                flex-direction: row;
              `}
            >
              {isUpdated ? (
                <Link
                  to={`${ROUTES.REGISTRATION_PREVIEW.replace(
                    ':id',
                    scheduleId
                  )}?form_type=team`}
                  target='_blank'
                  rel='noopener noreferrer'
                  css={css`
                    border: 1px solid ${colors.PRIMARY};
                    border-radius: 8px;
                    padding: 4px 8px;
                    justify-content: flex-start;
                    align-items: center;
                    gap: 8px;
                    display: flex;
                    cursor: pointer;
                    text-decoration: none;
                    &:hover {
                      opacity: 0.8;
                    }
                  `}
                >
                  <PreviewIcon />
                  <Text
                    variant='paragraphSmall'
                    weight='medium'
                    color={colors.PRIMARY}
                  >
                    Preview Form
                  </Text>
                </Link>
              ) : (
                <Tooltip
                  title={
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      color={colors.NEUTRAL_0}
                    >
                      Please save the registration form first to preview the
                      form
                    </Text>
                  }
                  placement='bottom'
                  zIndex={10010}
                >
                  <div
                    css={css`
                      border: 1px solid ${colors.NEUTRAL_30};
                      border-radius: 8px;
                      padding: 4px 8px;
                      justify-content: flex-start;
                      align-items: center;
                      gap: 8px;
                      display: flex;
                      cursor: not-allowed;
                      opacity: 0.5;
                    `}
                  >
                    <PreviewIcon color={colors.NEUTRAL_30} />
                    <Text
                      variant='paragraphSmall'
                      weight='medium'
                      color={colors.NEUTRAL_30}
                    >
                      Preview Form
                    </Text>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Append-mode Add Section form (empty state / header) */}
        {showForm && insertAfterId === null && renderAddSectionForm()}

        {/* Append-mode Add Content form (empty state / header) */}
        {showContentForm && insertAfterId === null && renderAddContentForm()}

        {/* Sections */}
        {sections.length > 0 ? (
          <div
            css={css`
              align-self: stretch;
              flex-direction: column;
              justify-content: flex-start;
              align-items: stretch;
              gap: 16px;
              display: flex;
            `}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext
                items={sections.map((section) => section.section_id)}
                strategy={verticalListSortingStrategy}
              >
                {sections.map((section) => (
                  <Fragment key={section.section_id}>
                    <DraggableSection
                      ref={(ref) =>
                        (sectionRefs.current[section.section_id] = ref)
                      }
                      id={section.section_id}
                      title={section.title}
                      subtitle={section.subtitle}
                      isEnabled={section.enabled}
                      questions={section.questions}
                      onToggle={handleToggle}
                      onEdit={handleEdit}
                      onDelete={handleDeleteSection}
                      onNewQuestion={handleNewQuestion}
                      onUpdateQuestions={handleUpdateQuestions}
                      onQuestionToggle={handleQuestionToggle}
                      onQuestionDelete={handleQuestionDelete}
                      onAddQuestion={handleAddQuestion}
                      onQuestionUpdate={handleQuestionUpdate}
                      customError={state.customError}
                      fieldErrors={state.fieldErrors}
                      isReserved={section.is_reserved}
                      isContent={section.is_content}
                      content={section.content}
                      onInsertSection={handleInsertSection}
                      onInsertContent={handleInsertContent}
                    />
                    {insertAfterId === section.section_id &&
                      showForm &&
                      renderAddSectionForm()}
                    {insertAfterId === section.section_id &&
                      showContentForm &&
                      renderAddContentForm()}
                  </Fragment>
                ))}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeSection ? (
                  <DraggableSection
                    key={`overlay-${activeSection.section_id}`}
                    id={activeSection.section_id}
                    title={activeSection.title}
                    subtitle={activeSection.subtitle}
                    questions={activeSection.questions}
                    isReserved={activeSection.is_reserved}
                    isContent={activeSection.is_content}
                    content={activeSection.content}
                    onEdit={() => {}}
                    onDelete={() => {}}
                    onNewQuestion={() => {}}
                    customError={{}}
                    fieldErrors={{}}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        ) : (
          <div
            css={css`
              align-self: stretch;
              padding: 32px;
              background: ${colors.SECONDARY_400};
              border-radius: 8px;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              gap: 16px;
              display: flex;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.NEUTRAL_30}
            >
              No sections added yet. Click &quot;Add&quot; to get started.
            </Text>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'section',
                    label: (
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                          padding: 4px 0;
                        `}
                      >
                        <Text
                          variant='paragraphSmall'
                          weight='regular'
                          color={colors.NEUTRAL_0}
                        >
                          Add Section
                        </Text>
                      </div>
                    ),
                    onClick: handleAddSection,
                  },
                  {
                    key: 'content',
                    label: (
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                          padding: 4px 0;
                        `}
                      >
                        <Text
                          variant='paragraphSmall'
                          weight='regular'
                          color={colors.NEUTRAL_0}
                        >
                          Add Content
                        </Text>
                      </div>
                    ),
                    onClick: handleAddContent,
                  },
                ],
              }}
              trigger={['click']}
              placement='bottom'
            >
              <div
                css={css`
                  border: 1px solid ${colors.PRIMARY};
                  border-radius: 8px;
                  padding: 8px 16px;
                  display: inline-flex;
                  align-items: center;
                  gap: 8px;
                  cursor: pointer;
                  &:hover {
                    opacity: 0.8;
                  }
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.PRIMARY}
                >
                  + Add
                </Text>
              </div>
            </Dropdown>
          </div>
        )}

        {/* Delete Section Modal */}
        <DeleteSectionModal
          isOpen={isDeleteModalOpen}
          sectionTitle={sectionToDelete?.title || ''}
          isLoading={isDeleting}
          error={deleteError}
          onConfirm={handleConfirmDelete}
          onClose={handleCancelDelete}
          isContent={sectionToDelete?.is_content || false}
        />
        <DeleteQuestionModal
          isOpen={isDeleteQuestionModalOpen}
          questionLabel={questionToDelete?.label || ''}
          isLoading={isDeletingQuestion}
          error={deleteQuestionError}
          onConfirm={handleConfirmQuestionDelete}
          onClose={handleCancelQuestionDelete}
        />
      </div>
    )
  }
)

export default TeamCustomForm
